import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { eventDetail, eventTypeText, formatDate, makeId, shortDate, today } from './helpers';
import { help } from './helpTexts';
import { launchColors } from './launchTheme';
import { InternalNotification, Workspace } from './types';
import SectionCard from './ui/SectionCard';
import { StatusPill } from './ui/StatusPill';

interface Props {
  workspace: Workspace;
  notifications: InternalNotification[];
  isOwner: boolean;
  busy: boolean;
  onSnapshot: (command: Record<string, unknown>) => void;
  onOpenActivity: (id: string) => void;
}

const percent = (value: number | null, unknown: string) => (value == null ? unknown : `${Math.round(value)} %`);

const LaunchHistory: React.FC<Props> = ({ workspace, notifications, isOwner, busy, onSnapshot, onOpenActivity }) => {
  const [snapshotOpen, setSnapshotOpen] = useState(false);
  const [correctionOfId, setCorrectionOfId] = useState('');
  const [reason, setReason] = useState('');
  const [asOf, setAsOf] = useState(today());
  const referenceDate = workspace.timeline.asOf;
  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.4fr 1fr' }, gap: 2, alignItems: 'start' }}>
        <SectionCard
          title="Händelser"
          info={help.historyEvents}
          subtitle={`${workspace.history.length} registrerade`}
        >
          {workspace.history.length === 0 && <Typography sx={{ fontSize: 13, color: launchColors.textSecondary }}>Ingen historik ännu.</Typography>}
          <Stack component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {[...workspace.history].reverse().map((event, index) => (
              <Box
                component="li"
                key={`${event.commandId}-${index}`}
                sx={{ display: 'grid', gridTemplateColumns: '64px 1fr', gap: 1.5, py: 1, borderBottom: `1px solid ${launchColors.divider}`, '&:last-of-type': { borderBottom: 'none' } }}
              >
                <Typography sx={{ fontSize: 12, color: launchColors.textSecondary, fontVariantNumeric: 'tabular-nums', pt: 0.25 }}>{shortDate(event.actuallyOccurredAt, referenceDate)}</Typography>
                <Box sx={{ minWidth: 0 }}>
                  {event.activityId ? (
                    <ButtonBase onClick={() => event.activityId && onOpenActivity(event.activityId)} sx={{ fontSize: 14, fontWeight: 600, color: launchColors.navy, textAlign: 'left', borderRadius: 1, '&:hover': { textDecoration: 'underline' } }}>
                      {eventTypeText(event.type)}
                    </ButtonBase>
                  ) : (
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{eventTypeText(event.type)}</Typography>
                  )}
                  {eventDetail(event) && <Typography sx={{ fontSize: 13, color: launchColors.textSecondary, overflowWrap: 'anywhere' }}>{eventDetail(event)}</Typography>}
                </Box>
              </Box>
            ))}
          </Stack>
        </SectionCard>

        <Stack spacing={2}>
          <SectionCard
            title="Veckoobservationer"
            info={help.historySnapshots}
            action={isOwner ? <Button size="small" variant="outlined" onClick={() => setSnapshotOpen(true)}>Spara observation</Button> : undefined}
          >
            {workspace.weeklyHistory.originals.length === 0 && <Typography sx={{ fontSize: 13, color: launchColors.textSecondary }}>Inga veckoobservationer sparade.</Typography>}
            <Stack spacing={1.5}>
              {workspace.weeklyHistory.originals.map((snapshot) => (
                <Box key={snapshot.id}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{formatDate(snapshot.asOf)} · utfall {percent(snapshot.actualPercent, 'okänt')}</Typography>
                  <Typography sx={{ fontSize: 13, color: launchColors.textSecondary }}>
                    Plan {percent(snapshot.plannedPercent, 'saknas')} · {snapshot.knownLateCount} kända sena · {snapshot.unknownCount} okända
                  </Typography>
                  {(workspace.weeklyHistory.revisionsByOriginal[snapshot.id] || []).map((revision) => (
                    <Alert key={revision.id} severity="info" sx={{ mt: 1 }}>Rättning {formatDate(revision.asOf)}: {revision.correctionReason}</Alert>
                  ))}
                </Box>
              ))}
            </Stack>
          </SectionCard>

          <SectionCard title="Plan- och omfattningsversioner" info={help.historyPlans} subtitle={`Planversion ${workspace.planVersion} · omfattningsversion ${workspace.scopeVersion}`}>
            {workspace.planChangeProposals.length === 0 && <Typography sx={{ fontSize: 13, color: launchColors.textSecondary }}>Inga planförslag.</Typography>}
            <Stack spacing={1}>
              {workspace.planChangeProposals.map((proposal) => (
                <Stack key={proposal.id} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{proposal.kind === 'scope' ? 'Omfattning' : 'Kedjeplan'}</Typography>
                      <StatusPill
                        label={proposal.status === 'committed' ? 'Fastställd' : 'Väntar'}
                        fg={proposal.status === 'committed' ? launchColors.mossDark : launchColors.warningText}
                        bg={proposal.status === 'committed' ? launchColors.mossLight : launchColors.warningBg}
                      />
                    </Stack>
                    <Typography sx={{ fontSize: 13, color: launchColors.textSecondary }}>{proposal.reason}</Typography>
                  </Box>
                  {isOwner && proposal.status === 'pending' && (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={busy}
                      onClick={() => onSnapshot({ type: 'commitPlanChange', proposalId: proposal.id })}
                    >Fastställ ny grundplan</Button>
                  )}
                </Stack>
              ))}
            </Stack>
          </SectionCard>

          {notifications.length > 0 && (
            <SectionCard title="Interna uppmärksamhetspunkter" info={help.historyNotices}>
              <Stack spacing={0.5}>
                {notifications.map((item) => (
                  <ButtonBase key={item.dedupeKey} onClick={() => onOpenActivity(item.activityId)} sx={{ justifyContent: 'flex-start', textAlign: 'left', fontSize: 13, color: launchColors.navy, borderRadius: 1, '&:hover': { textDecoration: 'underline' } }}>
                    {item.title}
                  </ButtonBase>
                ))}
              </Stack>
            </SectionCard>
          )}
        </Stack>
      </Box>

      <Dialog open={snapshotOpen} onClose={() => setSnapshotOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Spara veckoobservation</DialogTitle>
        <DialogContent><Stack spacing={1.5} sx={{ mt: 1 }}>
          <TextField type="date" label="Avser datum" InputLabelProps={{ shrink: true }} value={asOf} onChange={(event) => setAsOf(event.target.value)} />
          <TextField select label="Typ" value={correctionOfId} onChange={(event) => setCorrectionOfId(event.target.value)}>
            <MenuItem value="">Ny originalobservation</MenuItem>
            {workspace.weeklyHistory.originals.map((snapshot) => <MenuItem key={snapshot.id} value={snapshot.id}>Rätta {formatDate(snapshot.asOf)}</MenuItem>)}
          </TextField>
          {correctionOfId && <TextField required label="Orsak till rättning" value={reason} multiline minRows={2} onChange={(event) => setReason(event.target.value)} />}
        </Stack></DialogContent>
        <DialogActions><Button onClick={() => setSnapshotOpen(false)}>Avbryt</Button><Button variant="contained" disabled={busy || !asOf || Boolean(correctionOfId && !reason.trim())} onClick={() => {
          onSnapshot(correctionOfId ? {
            type: 'correctWeeklySnapshot', snapshotId: makeId('snapshot'), originalSnapshotId: correctionOfId, asOf, reason: reason.trim(),
          } : { type: 'captureWeeklySnapshot', snapshotId: makeId('snapshot'), asOf });
          setSnapshotOpen(false);
        }}>Spara</Button></DialogActions>
      </Dialog>
    </Stack>
  );
};

export default LaunchHistory;
